/*
 * PBK About Feature — pin スクロールで step 送り
 * 仕様(CL 2026-07-18): 左テキストは上下に動かさずその場で入替。右画像はマスク範囲を
 *   下→上に動かして次の写真を出す。
 * 2026-07-26 追加(ユーザー FB): 枠内の画像もスクロールに合わせて徐々に上へ流す（パララックス）。
 *   pin 中は枠が画面に固定されるため汎用 pbk-parallax.js の「枠の viewport 位置」式では動かない
 *   → ここで pin 進捗 p から translate を与える。振れ幅は img が枠からはみ出している量（CSS の
 *   top/height で ±46px・SP ±20px）を実測して使うので、値の正本は CSS 側。
 * 2026-07-28 修正(CL #140): 各 step に「静止(hold)」区間を作る。従来は u = p*N の線形で、
 *   step j の画像リビール窓が u∈[j-0.5, j+0.5] と隣と連続していたため、画像が全面になった瞬間に
 *   次のワイプが始まり「画像＋対応テキストが揃った状態」が一瞬しか無かった（= 2枚目が早く流れる）。
 *   pin 全長を [hold0][trans0→1][hold1][trans1→2]…[holdN-1] に配分し直す。全 step 一律。
 *   track の総高は変えない → スクロール総量は据え置き（#128 の見切れ対策と干渉しない）。
 * 2026-07-28 追加(CL #143): サムネ列をそのままステップ切替ナビに。click で該当 step の hold 区間中央へ
 *   smooth scroll（進捗→scrollY 逆算）。スクロール駆動の設計自体は不変で、これは補助操作。
 * 実装: track(step数×1画面) 内で pin(sticky 1画面)。scroll 進捗 p(0→1) を phaseAt() で
 *   { base(全面表示中 step) / t(次 step のワイプ進捗) / active(テキスト step) / vis(視覚進捗) } に変換し
 *   - active step の左パネル opacity を入替（位置不動）
 *   - shot を clip-path inset(下→上) でリビール（マスクのみ）。base 以下は全面・base+1 が t で開く
 *   - 各 img を +max → -max へ translate（vis に比例＝hold 中は完全静止）
 *   base(先頭) shot は常時全面。
 * 進捗の分母は window.innerHeight ではなく pin の実寸（CSS が dvh / min-height:640px を持つので
 *   画面高から引くと track と単位が食い違う。#128 の dvh 化と揃える）。
 */
(function () {
  'use strict';

  function initFeature(root) {
    if (root.dataset.aboutFeatInit === '1') return;
    root.dataset.aboutFeatInit = '1';

    var track = root.querySelector('.pbk-about-feature__track');
    var pin = root.querySelector('.pbk-about-feature__pin');
    var steps = Array.prototype.slice.call(root.querySelectorAll('.pbk-about-feature__step'));
    if (!track || steps.length === 0) return;
    var shots = steps.map(function (s) { return s.querySelector('.pbk-about-feature__shot'); });
    var photos = shots.map(function (s) { return s ? s.querySelector('img') : null; });
    var N = steps.length;

    // pin 全長の配分（step あたり 1 cell = hold + trans）。hold:trans = 1:1。
    // N=3 なら UNITS=5 → スクロール 200vh を hold 40vh ×3 / trans 40vh ×2 に分配。
    // 従来はリビール窓が 66.7vh ×3 で hold ゼロだった。
    var HOLD_W = 1;
    var TRANS_W = 1;
    var CELL = HOLD_W + TRANS_W;
    var UNITS = N * HOLD_W + (N - 1) * TRANS_W; // p=1 に対応する u
    // 切替区間のどこでテキストを入替えるか。CSS の opacity 0.45s フェードが
    // 区間終わり（＝画像が全面になる時点）までに収まるよう手前寄りにする。
    var SWAP_AT = 0.35;

    // p(0..1) → 位相。base=全面表示中の step / t=base+1 のワイプ進捗(0=hold 中) /
    //   active=テキストを出す step / vis=hold で止まる視覚進捗(0..1)
    function phaseAt(p) {
      var u = p * UNITS;
      var j = Math.floor(u / CELL);
      if (j < 0) j = 0;
      if (j > N - 1) j = N - 1;
      var w = u - j * CELL; // cell 内位置
      var t = 0;
      if (j < N - 1 && w > HOLD_W) {
        t = (w - HOLD_W) / TRANS_W;
        if (t > 1) t = 1;
      }
      var active = t >= SWAP_AT ? j + 1 : j;
      if (active > N - 1) active = N - 1;
      return { base: j, t: t, active: active, vis: N > 1 ? (j + t) / (N - 1) : 0.5 };
    }

    // step k の hold 区間中央に対応する p（サムネナビの着地点）
    function pForStep(k) {
      return (k * CELL + HOLD_W / 2) / UNITS;
    }

    // pin は CSS で dvh / min-height を持つので実寸を読む（画面高で代用すると track と食い違う）
    function scrollableLen() {
      var len = track.offsetHeight - (pin ? pin.offsetHeight : window.innerHeight);
      return len > 0 ? len : 1;
    }

    var ticking = false;
    // パララックスは装飾 → reduced-motion / data-static(ハーネス撮影) では中央静止。
    // clip-path のリビールはスクロール位置そのものの表現なので従来どおり常時動かす。
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    function staticMotion() {
      return reduce.matches ||
        document.documentElement.hasAttribute('data-static') ||
        document.body.hasAttribute('data-static');
    }

    // pin + マスクリビールは PC/SP 共通（CL 指示: SP も追従動作）。レイアウトのみ CSS で出し分け。
    function update() {
      ticking = false;

      var scrollable = scrollableLen();
      var top = track.getBoundingClientRect().top;
      var p = -top / scrollable;
      p = p < 0 ? 0 : p > 1 ? 1 : p;

      var ph = phaseAt(p);
      var active = ph.active;
      var still = staticMotion();

      for (var j = 0; j < N; j++) {
        if (steps[j].classList.contains('is-active') !== (j === active)) {
          steps[j].classList.toggle('is-active', j === active);
        }
        var shot = shots[j];
        if (!shot) continue;
        // 下→上リビール: 表示割合 rj 0→1 で 上 inset 100%→0%。
        // base 以下は全面（先頭 shot は常に base 以下＝常時全面）・base+1 だけがワイプ中・以降は未表示。
        var rj = j <= ph.base ? 1 : j === ph.base + 1 ? ph.t : 0;
        shot.style.clipPath = 'inset(' + ((1 - rj) * 100).toFixed(2) + '% 0 0 0)';

        // パララックス: 視覚進捗 vis 全体で +max → -max（下スクロールで画像は上へ）。
        // vis は hold 中は据え置き → 一拍の間は画像も完全静止（CL #140 の「目に入りやすい」）。
        // 振れ幅は CSS の --fx-parallax が正本（img の top/height も同じ変数から導出）。
        // 実寸から (img 高 - 枠高)/2 を推定すると、SP のように img の包含ブロックが枠と別要素の時に
        // 桁違いの値になり、丸め誤差では枠下に余白が出る
        var img = photos[j];
        if (!img) continue;
        if (still) { img.style.transform = ''; continue; }
        var max = parseFloat(getComputedStyle(shot).getPropertyValue('--fx-parallax')) || 0;
        img.style.transform = max > 0.5
          ? 'translate3d(0,' + ((0.5 - ph.vis) * 2 * max).toFixed(2) + 'px,0)'
          : '';
      }
    }

    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }

    // サムネナビ（CL #143）: step k の hold 区間中央へ。scrollY は進捗の逆算で出すので
    // hold/trans の配分を変えてもナビの着地点は自動で追従する。
    function goToStep(k) {
      if (k < 0) k = 0;
      if (k > N - 1) k = N - 1;
      var scrollable = scrollableLen();
      var docTop = track.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0);
      var y = Math.round(docTop + pForStep(k) * scrollable);
      // reduced-motion / ハーネス撮影中は smooth を使わず即着地
      if (!staticMotion() && 'scrollBehavior' in document.documentElement.style) {
        window.scrollTo({ top: y, behavior: 'smooth' });
      } else {
        window.scrollTo(0, y);
      }
    }

    root.addEventListener('click', function (e) {
      var el = e.target;
      var btn = el && el.closest ? el.closest('[data-feat-goto]') : null;
      if (!btn || !root.contains(btn)) return;
      var k = parseInt(btn.getAttribute('data-feat-goto'), 10);
      if (isNaN(k)) return;
      e.preventDefault();
      goToStep(k);
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-about-feature]');
    for (var i = 0; i < roots.length; i++) initFeature(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
